import { Center, Spinner } from "@chakra-ui/react";
import { Heading } from "@equidefi/ui";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router";

import { useProcessStripePayment } from "@/hooks/useInvestments";

import InvestmentDetails from "./v2/InvestmentDetails";

/**
 * Handles Stripe payment redirects: when Stripe redirects back to the
 * vault with `payment_intent` in the URL, we call `processPayment` to ensure
 * the payment is recorded in the workflow (avoiding a race with the webhook).
 */
const InvestmentDetailsWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const effectRan = useRef(false);

  const paymentIntentId = searchParams.get("payment_intent");
  const redirectStatus = searchParams.get("redirect_status");
  const isStripeRedirect = Boolean(paymentIntentId);

  const clearStripeParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("payment_intent");
    next.delete("payment_intent_client_secret");
    next.delete("redirect_status");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const processPayment = useProcessStripePayment(id ?? "", {
    onSettled: () => {
      // Clear Stripe params from URL once processed
      clearStripeParams();
    },
  });

  useEffect(() => {
    // Prevent double processing on mount. We only want to process once
    if (!paymentIntentId || effectRan.current) return;
    effectRan.current = true;

    if (redirectStatus === "succeeded") {
      processPayment.mutate(paymentIntentId);
    } else {
      // Non-success redirect (e.g. failed/requires_action) — just clear params
      clearStripeParams();
    }
  }, [paymentIntentId, redirectStatus, processPayment.mutate, clearStripeParams]);

  if (isStripeRedirect && processPayment.isLoading) {
    return (
      <Center h="60vh" flexDirection="column" gap={4}>
        <Heading fontWeight="bold" textStyle="h2">
          Processing Payment...
        </Heading>
        <Spinner size="xl" />
      </Center>
    );
  }

  return <InvestmentDetails />;
};

export default InvestmentDetailsWrapper;
